import type { DocumentData } from 'firebase/firestore'

export type VideoProps = Partial<
    Pick<DocumentData, 'contentURL' | 'contentURLType' | 'image' | 'recordingURL' | 'recordingURLType'>
>

export const getYoutubeVideoId = (url: string): string | undefined => {
    if (/youtu\.?be/.test(url)) {
        // Look first for known patterns
        let i
        const patterns = [
            /youtu\.be\/([^#&?]{11})/, // youtu.be/<id>
            /\?v=([^#&?]{11})/, // ?v=<id>
            /&v=([^#&?]{11})/, // &v=<id>
            /embed\/([^#&?]{11})/, // embed/<id>
            /\/v\/([^#&?]{11})/ // /v/<id>
        ]

        // If any pattern matches, return the ID
        for (i = 0; i < patterns.length; ++i) {
            if (patterns[i]?.test(url)) {
                return patterns[i]?.exec(url)![1]
            }
        }

        return undefined
    }

    return undefined
}

export const getFacebookVideoId = (url: string): string | undefined => {
    if (url.includes('facebook.com') || url.includes('fb.watch')) {
        if (url.includes('/videos/')) {
            return url.split('/videos/')[1]
        }

        if (url.includes('v=')) {
            return url.split('=')[1]
        }

        return undefined
    }

    return undefined
}

export const parseYoutubeVideoUrl = (url: string, type: string): VideoProps | undefined => {
    const id = getYoutubeVideoId(url)

    if (id) {
        if (type === 'content') {
            return {
                contentURLType: 'youtube',
                contentURL: `https://www.youtube.com/embed/${id}`
            }
        } else if (type === 'recording') {
            return {
                recordingURLType: 'youtube',
                recordingURL: `https://www.youtube.com/embed/${id}`
            }
        }

        return undefined
    }

    return undefined
}

export const parseFacebookVideoUrl = (url: string, type: string): VideoProps | undefined => {
    const id = getFacebookVideoId(url)
    const isFBShortener = url.includes('fb.watch')

    if (id || isFBShortener) {
        if (type === 'content') {
            return {
                contentURLType: 'facebook',
                contentURL: isFBShortener ? url : `https://www.facebook.com/facebook/videos/${id}`
            }
        } else if (type === 'recording') {
            return {
                recordingURLType: 'facebook',
                recordingURL: isFBShortener ? url : `https://www.facebook.com/facebook/videos/${id}`
            }
        }

        return undefined
    }

    return undefined
}

export const useRawVideoProps = (url: string): VideoProps => ({
    contentURLType: '',
    contentURL: url
})

export const getEmbededVideo = (videoProps: VideoProps): string =>
    videoProps.contentURLType === 'facebook'
        ? `https://www.facebook.com/plugins/video.php?href=${videoProps.contentURL}&show_text=0&width=560`
        : videoProps.contentURL

export const getEmbededRecording = (videoProps: VideoProps): string =>
    videoProps.recordingURLType === 'facebook'
        ? `https://www.facebook.com/plugins/video.php?href=${videoProps.recordingURL}&show_text=0&width=560`
        : videoProps.recordingURL
